/* lib/recurringConsentText.js — текст условий автопродления, под которым
   плательщик ставит галочку (components/patterns/RecurringConsentModal.jsx).

   Отдельно от recurringConsent.js, потому что тексту нужны форматтеры
   (format.js трогает window при загрузке), а правила тянет слой API.
   При правке СМЫСЛА текста — поднять RECURRING_CONSENT_VERSION. */

import { fmt } from './format.js';

/* «30 дней» — срок периода словами. Периоды у тарифов 30 / 90 / 180 / 365 дней;
   «месяц» здесь не пишем: списание идёт через N дней, а не в то же число. */
function periodWords(days) {
  const n = Number(days) > 0 ? Number(days) : 30;
  return n + ' ' + fmt.plural(n, 'день', 'дня', 'дней');
}

/* Условия автопродления одним абзацем.
   priceRub — цена ТАРИФА (её и спишет продление), а не сумма первого платежа:
   у апгрейда первый платёж — доплата, у ссылки — сумма, названная менеджером.
   guestPays — платит не владелец аккаунта (счёт по ссылке): настроек у него
   нет, а списываться будет ЕГО карта. */
function consentTerms({ priceRub, periodDays, guestPays = false }) {
  const price = Number(priceRub);
  const charge = Number.isFinite(price) && price > 0
    ? 'стоимость тарифа — ' + fmt.money(price)
    : 'стоимость тарифа';
  const main = 'Подписка продлевается автоматически: по окончании каждого оплаченного периода ('
    + periodWords(periodDays) + ') с карты, которой вы сейчас оплатите, списывается ' + charge + '.';
  const off = guestPays
    ? ' Отключить продление может владелец аккаунта в настройках тарифа — до отключения списания продолжаются.'
    : ' Отключить продление можно в любой момент: «Настройки» → тариф → «Отменить продление подписки».';
  return main + off;
}

/* Условия перехода на тариф дешевле (понижение) одним абзацем: первое списание —
   в день окончания текущего тарифа, с УЖЕ привязанной карты, и тариф сменится
   только им. startsOn — дата словами («14.11.2026»). При правке смысла — поднять
   PLAN_CHANGE_CONSENT_VERSION (lib/recurringConsent.js). */
function planChangeTerms({ priceRub, periodDays, startsOn }) {
  const price = Number(priceRub);
  const charge = Number.isFinite(price) && price > 0
    ? 'стоимость нового тарифа — ' + fmt.money(price)
    : 'стоимость нового тарифа';
  const when = startsOn ? startsOn : 'дату окончания текущего тарифа';
  return 'До ' + when + ' действует текущий тариф. В этот день с привязанной карты '
    + 'списывается ' + charge + ', и подписка переходит на новый тариф; дальше списание '
    + 'повторяется по окончании каждого оплаченного периода (' + periodWords(periodDays) + '). '
    + 'Если оплата не пройдёт, тариф не сменится, а после трёх дней льготного периода '
    + 'доступ к платным разделам закроется. Передумать можно до ' + when + ': «Настройки» → '
    + 'тариф → «Отменить» у запланированного перехода.';
}

/* Пояснение над окном согласия, открытым заново после 409 price_changed: цена тарифа
   сменилась, пока окно было открыто, и галочку ставят ещё раз — уже под новой ценой.
   wasRub — цена, которую человек видел; nowRub — актуальная (из ответа сервера).
   Без wasRub цены в окне не было (экран её не знал и не отправил — сервер в таком случае
   тоже отвечает price_changed): «изменилась» было бы неправдой, называем только цену. */
function priceChangedNotice(wasRub, nowRub) {
  const known = (v) => v != null && v !== '' && Number.isFinite(Number(v)) && Number(v) > 0;
  const now = known(nowRub) ? fmt.money(Number(nowRub)) : null;
  if (!known(wasRub)) {
    return (now ? 'Цена тарифа — ' + now + '. ' : '') + 'Проверьте условия и подтвердите переход.';
  }
  const what = now
    ? 'Цена тарифа изменилась: было ' + fmt.money(Number(wasRub)) + ', стало ' + now + '.'
    : 'Цена тарифа изменилась.';
  return what + ' Проверьте условия и подтвердите переход заново.';
}

export { periodWords, consentTerms, planChangeTerms, priceChangedNotice };
